import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import type { IPublicClientApplication } from "@azure/msal-browser";
import { RequireAuth } from "../auth/RequireAuth";
import { IntakeCountsProvider } from "./IntakeCountsContext";
import { useAppUser } from "./AppUserContext";
import { ErrorBoundary } from "./ErrorBoundary";
import { Shell } from "./Shell";
import { Landing } from "./Landing";
import { PATHS } from "./paths";
import { DataView } from "../lanes/portal/DataView";
import { AdminUsers } from "../lanes/admin/AdminUsers";
import { ConsultantCockpit } from "../lanes/cockpit/ConsultantCockpit";
import { NomenclaturePage } from "../lanes/nomenclature/NomenclaturePage";
import { FacilitiesPage } from "../lanes/facilities/FacilitiesPage";
import { PolymerGroupsPage } from "../lanes/facilities/PolymerGroupsPage";
import "../styles/tokens.css";

function AdminOnly({ children }: { children: ReactNode }) {
  const { appRole } = useAppUser();
  if (appRole !== "Admin") return <Navigate to={PATHS.intakeLane("spend")} replace />;
  return <>{children}</>;
}

export default function App({ msalInstance }: { msalInstance: IPublicClientApplication }) {
  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <RequireAuth>
          <IntakeCountsProvider>
            <Routes>
              <Route path={PATHS.home} element={<Landing />} />
              <Route path={PATHS.intake} element={<Navigate to={PATHS.intakeLane("spend")} replace />} />
              <Route path={PATHS.intakeLane(":lane")} element={<Shell />} />
              <Route path={PATHS.data} element={<DataView />} />
              <Route path={PATHS.cockpit} element={<ConsultantCockpit />} />
              <Route path={PATHS.nomenclature} element={<NomenclaturePage />} />
              <Route path={PATHS.plantRegistry} element={<FacilitiesPage />} />
              <Route path={PATHS.polymerGroups} element={<PolymerGroupsPage />} />
              <Route path={PATHS.adminUsers} element={<AdminOnly><AdminUsers /></AdminOnly>} />
              <Route path="*" element={<Navigate to={PATHS.home} replace />} />
            </Routes>
          </IntakeCountsProvider>
        </RequireAuth>
      </MsalProvider>
    </ErrorBoundary>
  );
}
